import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { useAuth } from "../context/AuthContext";
import { company } from "../mock";
import api from "../api";

const HIGHLIGHTS = [
  { value: "Secure", label: "Supabase sign-in" },
  { value: "Live", label: "Onboarding tracking" },
  { value: "Private", label: "Document storage" },
];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function HelpPanel({ support }) {
  return (
    <div className="mx-auto max-w-xs rounded-lg border border-slate-200 bg-slate-50 p-3 text-left text-xs text-slate-600" data-testid="need-help-panel">
      <div>Forgot your password? <Link to="/forgot-password" className="font-medium text-violet-600">Reset it here</Link>.</div>
      {support.email && <div className="mt-1">Email: <a href={`mailto:${support.email}`} className="font-medium text-violet-600">{support.email}</a></div>}
      {support.phone && <div className="mt-1">Phone: {support.phone}</div>}
      {support.hours && <div className="mt-1">Hours: {support.hours}</div>}
      {!support.email && !support.phone && <div className="mt-1 text-slate-400">Contact your Campbell Communications onboarding team.</div>}
    </div>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [show, setShow] = useState(false);
  const [remember, setRemember] = useState(true);
  const [help, setHelp] = useState(false);
  const [support, setSupport] = useState({});

  useEffect(() => { if (params.get("expired")) setError("Your session expired. Please sign in again."); }, [params]);
  useEffect(() => { api.get("/support/config").then((r) => setSupport(r.data)).catch(() => {}); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!EMAIL_RE.test(email.trim())) { setError("Please enter a valid email address."); return; }
    if (!password) { setError("Please enter your password."); return; }
    setBusy(true);
    try {
      const user = await login(email.trim(), password, remember);
      const role = user.role;
      const dest = (role === "client_admin" || role === "client_user") ? "/client-dashboard"
        : role === "applicant" ? "/dashboard"
        : (role === "supervisor" || role === "recruiter") ? "/supervisor/applicants"
        : "/metrics";
      navigate(dest);
    } catch (err) {
      setError(err.response ? (err.response.data?.detail || "Unable to sign in") : "Network error — check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen grid-cols-1 lg:grid-cols-2">
      <div className="hero-gradient relative hidden flex-col justify-between overflow-hidden p-10 text-white lg:flex">
        <div className="absolute inset-0 dot-grid opacity-40" />
        <div className="relative flex items-center gap-3">
          <img src={company.logo} alt={company.legal} className="h-12 w-12 rounded-xl bg-white object-cover p-1" />
          <div className="leading-tight">
            <div className="text-base font-bold">{company.name}</div>
            <div className="text-xs text-white/60">{company.legal}</div>
          </div>
        </div>
        <div className="relative max-w-md">
          <h1 className="text-4xl font-extrabold leading-tight">Onboard, train, and deploy your workforce in one place.</h1>
          <p className="mt-4 text-white/70">Documents, orientation, certifications, and assessments — tracked from day one to field ready.</p>
          <div className="mt-10 grid grid-cols-3 gap-6">
            {HIGHLIGHTS.map((s) => (
              <div key={s.label}>
                <div className="text-2xl font-bold">{s.value}</div>
                <div className="text-xs uppercase tracking-wide text-white/50">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="relative text-xs text-white/40">© 2026 {company.legal}</div>
      </div>

      <div className="relative flex items-center justify-center bg-background px-6 py-12">
        <div className="absolute inset-0 dot-grid opacity-60" />
        <div className="relative w-full max-w-md rounded-2xl border border-slate-200 bg-white p-8 shadow-xl shadow-slate-200/60">
          <div className="mb-6 flex items-center gap-3 lg:hidden">
            <img src={company.logo} alt={company.legal} className="h-10 w-10 rounded-lg object-cover" />
            <div className="text-sm font-bold text-slate-900">{company.name}</div>
          </div>
          <h2 className="text-2xl font-bold text-slate-900">Welcome back</h2>
          <p className="mt-1 text-sm text-slate-500">Sign in to your Campbell Connect account.</p>

          {error && <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700" data-testid="login-error">{error}</div>}

          <form onSubmit={handleSubmit} noValidate className="mt-6 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="login-email-input" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={show ? "text" : "password"} data-testid="login-password-input" placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" />
                <button type="button" onClick={() => setShow((v) => !v)} data-testid="toggle-password-visibility" aria-label={show ? "Hide password" : "Show password"} className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-slate-600">
                <Checkbox id="remember" checked={remember} onCheckedChange={(v) => setRemember(!!v)} data-testid="remember-me-checkbox" />
                Remember me
              </label>
              <Link to="/forgot-password" data-testid="forgot-password-link" className="cursor-pointer text-sm font-medium text-violet-600 hover:underline">Forgot password?</Link>
            </div>
            <Button type="submit" disabled={busy} data-testid="login-submit-button" className="btn-primary-gradient w-full border-0 text-white">
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="mt-6 space-y-2 text-center">
            <Link to="/signup" className="block text-sm font-medium text-violet-600 hover:underline" data-testid="signup-link">New Applicant? Create Account</Link>
            <div className="space-y-2 text-sm text-slate-500">
              <button type="button" onClick={() => setHelp((v) => !v)} data-testid="need-help-button" className="hover:text-slate-700">Need Help?</button>
              {help && <HelpPanel support={support} />}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
